// Puck template builder (Module L / MIS-872).
//
// The OSS `<Puck>` editor with the in-editor Puck Cloud AI chat plugin. Loads
// the template via `GET /api/ad/templates` (filtered to this id), mounts the
// editor over the shared component `config` (apps/web/src/ad/puck-config.tsx,
// which mirrors the server block contract), and saves on publish via
// `PATCH /api/ad/templates/:id`.
//
// The AI plugin (`createAiPlugin`) talks to the backend handler at `/api/puck/*`
// (apps/worker/src/routes/puck.ts → puckHandler), which proxies Puck Cloud. That
// path needs PUCK_API_KEY set on the Worker to actually generate; the editor +
// drag/drop + manual editing + save all work regardless.

import { Puck } from '@puckeditor/core';
import '@puckeditor/core/puck.css';
import { createAiPlugin } from '@puckeditor/plugin-ai';
import '@puckeditor/plugin-ai/styles.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { puckConfig } from '@/ad/puck-config';
// Dark theme for the Puck editor chrome (scoped to `.puck-dark` below), so the
// editor matches the dark dashboard instead of rendering Puck's default white UI.
import '@/ad/puck-dark.css';
import { useToast } from '@/components/ui/toast';
import { useApi } from '@/lib/api';

// Empty Puck document for a never-edited template.
const EMPTY_PUCK_DATA = { root: {}, content: [] } as const;

// The AI plugin instance (created once at module scope — it's stateless config).
const aiPlugin = createAiPlugin();

export const Route = createFileRoute('/_authenticated/templates/$id/builder')({
  component: TemplateBuilderPage,
});

interface LanderTemplate {
  id: number;
  name: string;
  puckJson: Record<string, unknown> | null;
  scope: 'org' | 'personal';
  status: 'draft' | 'published' | 'archived';
}

function TemplateBuilderPage(): React.ReactElement {
  const { id } = Route.useParams();
  const api = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ad', 'templates'],
    queryFn: async (): Promise<{ templates: LanderTemplate[] }> => {
      const res = await api.api.ad.templates.$get();
      if (!res.ok) throw new Error(`Failed to load templates (${res.status})`);
      return (await res.json()) as { templates: LanderTemplate[] };
    },
  });

  const template = data?.templates.find((t) => String(t.id) === id);

  // Save the editor's Puck Data via PATCH (the PATCH route parses the raw body,
  // so we send it through the typed `init` escape hatch — same pattern the
  // lander editor uses).
  const save = useMutation({
    mutationFn: async (puckJson: Record<string, unknown>): Promise<void> => {
      const res = await api.api.ad.templates[':id'].$patch(
        { param: { id } },
        { init: { body: JSON.stringify({ puckJson }) } },
      );
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ad', 'templates'] });
      toast({ title: 'Template saved' });
    },
    onError: (err) => {
      toast({ title: 'Save failed', description: String(err), variant: 'destructive' });
    },
  });

  if (isLoading) return <div className="p-6">Loading template…</div>;
  if (isError || !template) {
    return (
      <div className="p-6">
        <p>Template not found.</p>
        <Link to="/dashboard" className="underline">
          Back to dashboard
        </Link>
      </div>
    );
  }

  // `template.puckJson` is `Record<string,unknown> | null`; Puck wants its Data
  // shape. The data is validated server-side on save (isValidPuckData), so we
  // pass it through, defaulting an empty doc for a fresh template.
  const initialData = (template.puckJson ?? EMPTY_PUCK_DATA) as Parameters<typeof Puck>[0]['data'];

  return (
    <div className="puck-dark h-[calc(100vh-4rem)]">
      <Puck
        config={puckConfig}
        data={initialData}
        plugins={[aiPlugin]}
        onPublish={(data) => {
          save.mutate(data as unknown as Record<string, unknown>);
        }}
      />
    </div>
  );
}
